import { useMemo } from 'react';

import { getDebtCampaignVariables } from '../../../constants';
import { ColumnMapping, VariableDefinition } from '../../../types/column-mapping';
import { getUnmatchedVariables, isRequiredUnmatchedVariable } from '../../../utils';

interface UseUnmatchedFieldsParams {
  csvMappings: Record<string, ColumnMapping> | null;
  isShowingReduxData: boolean;
  /**
   * Names of variables that are conditionally required for this audience —
   * e.g. `residence_state` when any subscriber's country_code is US/BR.
   * Flips the matching variable's `required` flag to `true` in the returned
   * `unmatchedFields` so the warning UI routes them to "required not matched".
   */
  extraRequiredFields?: string[];
}

interface UseUnmatchedFieldsResult {
  unmatchedFields: VariableDefinition[];
  hasRequiredUnmatched: boolean;
}

export const useUnmatchedFields = ({
  csvMappings,
  isShowingReduxData,
  extraRequiredFields,
}: UseUnmatchedFieldsParams): UseUnmatchedFieldsResult => {
  const extraRequiredSet = useMemo(() => new Set(extraRequiredFields ?? []), [extraRequiredFields]);

  const unmatchedFields = useMemo(() => {
    // Only evaluate against a live upload — a saved-campaign reopen reconstructs mappings and would flag blank-on-all-rows fields as unmatched.
    if (!isShowingReduxData || !csvMappings) {
      return [];
    }
    return getUnmatchedVariables(csvMappings, getDebtCampaignVariables()).map((field) =>
      extraRequiredSet.has(field.name) ? { ...field, required: true } : field
    );
  }, [isShowingReduxData, csvMappings, extraRequiredSet]);

  const hasRequiredUnmatched = unmatchedFields.some((f) => isRequiredUnmatchedVariable(f));

  return { unmatchedFields, hasRequiredUnmatched };
};
